/**
 * @zendir/ui - ZenSpace3D Shaders
 *
 * Advanced GLSL shaders for atmosphere, glow effects, coverage visualization,
 * and other space rendering effects.
 */

// =============================================================================
// Atmosphere Shader (Enhanced from original)
// =============================================================================

export const atmosphereVertexShader = `
varying vec3 vNormal;
varying vec3 vPosition;
varying float vIntensity;

void main() {
  vNormal = normalize(normalMatrix * normal);
  vPosition = position;
  
  // Calculate intensity based on view angle
  vec4 worldPosition = modelMatrix * vec4(position, 1.0);
  vec3 viewDirection = normalize(cameraPosition - worldPosition.xyz);
  vIntensity = pow(0.65 - dot(vNormal, viewDirection), 2.0);
  
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

export const atmosphereFragmentShader = `
varying vec3 vNormal;
varying vec3 vPosition;
varying float vIntensity;

uniform vec3 glowColor;
uniform float glowIntensity;

void main() {
  // Fresnel-based glow
  float intensity = vIntensity * glowIntensity;
  
  // Add subtle color variation
  vec3 color = glowColor * intensity;
  
  // Fade at edges for smoother blending
  float alpha = intensity * 0.8;
  
  gl_FragColor = vec4(color, alpha);
}
`;

// =============================================================================
// Enhanced Stars Shader (Twinkling with nebula)
// =============================================================================

export const starsVertexShader = `
attribute float opacity;
attribute float size;
attribute vec3 customColor;

varying float vOpacity;
varying vec3 vColor;

uniform float time;
uniform float pointSize;

void main() {
  vOpacity = opacity;
  vColor = customColor;
  
  // Twinkle effect
  float twinkle = sin(time * (0.5 + opacity * 2.0) + opacity * 100.0) * 0.5 + 0.5;
  vOpacity = opacity * (0.6 + twinkle * 0.4);
  
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  
  // Size attenuation
  float sizeMultiplier = size > 0.0 ? size : 1.0;
  gl_PointSize = pointSize * sizeMultiplier * (300.0 / -mvPosition.z);
  gl_PointSize = clamp(gl_PointSize, 0.5, 8.0);
  
  gl_Position = projectionMatrix * mvPosition;
}
`;

export const starsFragmentShader = `
varying float vOpacity;
varying vec3 vColor;

void main() {
  // Circular point with soft edge
  vec2 center = gl_PointCoord - vec2(0.5);
  float dist = length(center);
  
  if (dist > 0.5) discard;
  
  // Soft glow falloff
  float alpha = smoothstep(0.5, 0.0, dist) * vOpacity;
  
  // Apply color
  vec3 color = vColor.r > 0.0 ? vColor : vec3(1.0, 1.0, 1.0);
  
  gl_FragColor = vec4(color, alpha);
}
`;

// =============================================================================
// Coverage Heatmap Shader
// =============================================================================

export const coverageVertexShader = `
varying vec2 vUv;
varying vec3 vNormal;
varying vec3 vPosition;

void main() {
  vUv = uv;
  vNormal = normalize(normalMatrix * normal);
  vPosition = (modelMatrix * vec4(position, 1.0)).xyz;
  
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

export const coverageFragmentShader = `
uniform sampler2D coverageMap;
uniform vec3 lowColor;
uniform vec3 highColor;
uniform float opacity;
uniform float time;

varying vec2 vUv;
varying vec3 vNormal;
varying vec3 vPosition;

void main() {
  // Sample coverage value
  float coverage = texture2D(coverageMap, vUv).r;
  
  // Animated pulse for active areas
  float pulse = sin(time * 2.0) * 0.1 + 0.9;
  coverage *= coverage > 0.5 ? pulse : 1.0;
  
  // Color gradient
  vec3 color = mix(lowColor, highColor, coverage);
  
  // Fresnel edge highlight
  vec3 viewDir = normalize(cameraPosition - vPosition);
  float fresnel = pow(1.0 - dot(vNormal, viewDir), 2.0);
  color += vec3(0.1, 0.3, 0.5) * fresnel * coverage;
  
  // Hexagon pattern overlay (subtle)
  float hexPattern = fract(vUv.x * 20.0 + vUv.y * 10.0);
  color *= 0.95 + hexPattern * 0.05;
  
  gl_FragColor = vec4(color, coverage * opacity);
}
`;

// =============================================================================
// Visibility Cone Shader
// =============================================================================

export const visibilityConeVertexShader = `
varying vec3 vPosition;
varying vec3 vNormal;
varying float vDistFromApex;

uniform vec3 apexPosition;

void main() {
  vPosition = (modelMatrix * vec4(position, 1.0)).xyz;
  vNormal = normalize(normalMatrix * normal);
  vDistFromApex = distance(position, apexPosition);
  
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

export const visibilityConeFragmentShader = `
uniform vec3 coneColor;
uniform float coneOpacity;
uniform float time;

varying vec3 vPosition;
varying vec3 vNormal;
varying float vDistFromApex;

void main() {
  // Fresnel effect for edge visibility
  vec3 viewDir = normalize(cameraPosition - vPosition);
  float fresnel = pow(1.0 - abs(dot(vNormal, viewDir)), 2.0);
  
  // Animated scan line effect
  float scan = fract(vDistFromApex * 0.001 - time * 0.5);
  scan = smoothstep(0.0, 0.1, scan) * smoothstep(0.2, 0.1, scan);
  
  // Distance-based opacity (fade towards apex)
  float distFade = smoothstep(0.0, 500.0, vDistFromApex);
  
  // Combine effects
  float alpha = (fresnel * 0.3 + 0.1 + scan * 0.2) * coneOpacity * distFade;
  
  gl_FragColor = vec4(coneColor, alpha);
}
`;

// =============================================================================
// Orbit Line Shader
// =============================================================================

export const orbitLineVertexShader = `
attribute float lineDistance;
varying float vLineDistance;
varying float vOpacity;

uniform float totalLength;
uniform float time;
uniform float dashScale;

void main() {
  vLineDistance = lineDistance;
  
  // Animated dash effect
  float animOffset = mod(time * 0.5, 1.0) * totalLength;
  float dashPos = mod(lineDistance + animOffset, dashScale);
  vOpacity = smoothstep(0.0, dashScale * 0.3, dashPos) * 
             smoothstep(dashScale, dashScale * 0.7, dashPos);
  
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

export const orbitLineFragmentShader = `
uniform vec3 lineColor;
uniform float lineOpacity;

varying float vLineDistance;
varying float vOpacity;

void main() {
  gl_FragColor = vec4(lineColor, lineOpacity * vOpacity);
}
`;

// =============================================================================
// Satellite/Object Glow Shader
// =============================================================================

export const objectGlowVertexShader = `
varying vec3 vNormal;
varying vec3 vViewPosition;

void main() {
  vNormal = normalize(normalMatrix * normal);
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  vViewPosition = -mvPosition.xyz;
  
  gl_Position = projectionMatrix * mvPosition;
}
`;

export const objectGlowFragmentShader = `
uniform vec3 glowColor;
uniform float glowIntensity;
uniform float glowPower;

varying vec3 vNormal;
varying vec3 vViewPosition;

void main() {
  vec3 viewDir = normalize(vViewPosition);
  float fresnel = pow(1.0 - abs(dot(vNormal, viewDir)), glowPower);
  
  vec3 color = glowColor * glowIntensity * fresnel;
  float alpha = fresnel * glowIntensity;
  
  gl_FragColor = vec4(color, alpha);
}
`;

// =============================================================================
// Selection Ring Shader
// =============================================================================

export const selectionRingVertexShader = `
varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

export const selectionRingFragmentShader = `
uniform vec3 ringColor;
uniform float time;
uniform float ringWidth;
uniform float ringRadius;

varying vec2 vUv;

void main() {
  vec2 center = vUv - vec2(0.5);
  float dist = length(center);
  
  // Ring shape
  float ring = smoothstep(ringRadius - ringWidth, ringRadius, dist) *
               smoothstep(ringRadius + ringWidth, ringRadius, dist);
  
  // Animated rotation
  float angle = atan(center.y, center.x);
  float rotatingDash = sin(angle * 8.0 - time * 3.0) * 0.5 + 0.5;
  
  // Pulse effect
  float pulse = sin(time * 2.0) * 0.2 + 0.8;
  
  float alpha = ring * rotatingDash * pulse;
  
  gl_FragColor = vec4(ringColor, alpha);
}
`;

// =============================================================================
// Terminator (Day/Night) Shader
// =============================================================================

export const terminatorVertexShader = `
varying vec3 vWorldPosition;
varying vec3 vNormal;

void main() {
  vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
  vNormal = normalize(normalMatrix * normal);
  
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

export const terminatorFragmentShader = `
uniform vec3 sunDirection;
uniform sampler2D dayTexture;
uniform sampler2D nightTexture;

varying vec3 vWorldPosition;
varying vec3 vNormal;

void main() {
  // Calculate sun illumination
  float sunDot = dot(vNormal, sunDirection);
  
  // Smooth transition at terminator
  float dayFactor = smoothstep(-0.1, 0.2, sunDot);
  
  // Sample textures (simplified - using world position for UV)
  vec2 uv = vec2(
    atan(vWorldPosition.z, vWorldPosition.x) / (2.0 * 3.14159) + 0.5,
    asin(vWorldPosition.y / length(vWorldPosition)) / 3.14159 + 0.5
  );
  
  vec4 dayColor = texture2D(dayTexture, uv);
  vec4 nightColor = texture2D(nightTexture, uv);
  
  // Blend day and night
  vec4 color = mix(nightColor, dayColor, dayFactor);
  
  // Add city lights glow on night side
  float nightGlow = (1.0 - dayFactor) * nightColor.r * 0.5;
  color.rgb += vec3(1.0, 0.9, 0.7) * nightGlow;
  
  gl_FragColor = color;
}
`;

// =============================================================================
// Grid Shader (Lat/Lon lines)
// =============================================================================

export const gridVertexShader = `
varying vec3 vPosition;

void main() {
  vPosition = position;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

export const gridFragmentShader = `
uniform vec3 gridColor;
uniform float gridOpacity;
uniform float gridSpacing;
uniform float lineWidth;

varying vec3 vPosition;

void main() {
  // Convert position to lat/lon
  float lat = asin(vPosition.y / length(vPosition)) * 57.2958; // rad to deg
  float lon = atan(vPosition.z, vPosition.x) * 57.2958;
  
  // Create grid lines
  float latLine = abs(mod(lat + 90.0, gridSpacing) - gridSpacing * 0.5);
  float lonLine = abs(mod(lon + 180.0, gridSpacing) - gridSpacing * 0.5);
  
  float line = min(latLine, lonLine);
  float alpha = smoothstep(lineWidth, 0.0, line) * gridOpacity;
  
  // Highlight equator and prime meridian
  float equator = smoothstep(1.0, 0.0, abs(lat));
  float primeMeridian = smoothstep(1.0, 0.0, abs(lon));
  alpha += (equator + primeMeridian) * 0.3;
  
  gl_FragColor = vec4(gridColor, alpha);
}
`;

// =============================================================================
// Planet Ring Shader (Saturn-like)
// =============================================================================

export const planetRingVertexShader = `
varying vec2 vUv;
varying vec3 vWorldPosition;

void main() {
  vUv = uv;
  vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
  
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

export const planetRingFragmentShader = `
uniform vec3 ringColor;
uniform float innerRadius;
uniform float outerRadius;
uniform float ringOpacity;
uniform vec3 sunDirection;

varying vec2 vUv;
varying vec3 vWorldPosition;

void main() {
  vec2 center = vUv - vec2(0.5);
  float dist = length(center) * 2.0;
  
  // Ring bands
  float normalizedDist = (dist - innerRadius) / (outerRadius - innerRadius);
  
  if (normalizedDist < 0.0 || normalizedDist > 1.0) discard;
  
  // Create ring bands pattern
  float bands = sin(normalizedDist * 50.0) * 0.3 + 0.7;
  float gaps = step(0.3, fract(normalizedDist * 5.0));
  
  // Simple shadow from planet
  float shadow = smoothstep(0.3, 0.5, dist);
  
  float alpha = bands * gaps * ringOpacity * shadow;
  
  gl_FragColor = vec4(ringColor, alpha);
}
`;

// =============================================================================
// Debris Field Shader (for instanced rendering)
// =============================================================================

export const debrisVertexShader = `
attribute vec3 instancePosition;
attribute float instanceSize;
attribute vec3 instanceColor;

varying vec3 vColor;
varying float vSize;

uniform float time;

void main() {
  vColor = instanceColor;
  vSize = instanceSize;
  
  // Apply instance transform
  vec3 transformed = position * instanceSize + instancePosition;
  
  // Slight rotation animation
  float angle = time * 0.5 + instancePosition.x * 0.01;
  mat2 rot = mat2(cos(angle), -sin(angle), sin(angle), cos(angle));
  transformed.xz = rot * transformed.xz;
  
  gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);
}
`;

export const debrisFragmentShader = `
varying vec3 vColor;
varying float vSize;

void main() {
  gl_FragColor = vec4(vColor, 1.0);
}
`;

// =============================================================================
// Shader Uniforms Helpers
// =============================================================================

export interface AtmosphereUniforms {
  glowColor: { value: [number, number, number] };
  glowIntensity: { value: number };
}

export interface StarsUniforms {
  time: { value: number };
  pointSize: { value: number };
}

export interface CoverageUniforms {
  coverageMap: { value: unknown };
  lowColor: { value: [number, number, number] };
  highColor: { value: [number, number, number] };
  opacity: { value: number };
  time: { value: number };
}

export interface VisibilityConeUniforms {
  coneColor: { value: [number, number, number] };
  coneOpacity: { value: number };
  time: { value: number };
  apexPosition: { value: [number, number, number] };
}

export function createAtmosphereUniforms(
  color: [number, number, number] = [0.3, 0.6, 1.0],
  intensity: number = 1.5,
): AtmosphereUniforms {
  return {
    glowColor: { value: color },
    glowIntensity: { value: intensity },
  };
}

export function createStarsUniforms(pointSize: number = 2.0): StarsUniforms {
  return {
    time: { value: 0 },
    pointSize: { value: pointSize },
  };
}

export function createVisibilityConeUniforms(
  color: [number, number, number] = [0.2, 0.8, 0.9],
  opacity: number = 0.3,
): VisibilityConeUniforms {
  return {
    coneColor: { value: color },
    coneOpacity: { value: opacity },
    time: { value: 0 },
    apexPosition: { value: [0, 0, 0] },
  };
}
